import { useCallback, useEffect, useState } from "react";
import { Loader2, Megaphone, Pin, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Empty, Err, Panel } from "@/components/community/kit";
import { when } from "@/lib/community";

export const News = ({ community }) => {
  const cid = community.id;
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ title: "", body: "", pinned: false });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [composing, setComposing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/announcements`);
      setData(res);
      res.announcements.filter((a) => !a.read).forEach((a) => {
        api.post(`/communities/${cid}/announcements/${a.id}/read`).catch(() => {});
      });
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    setBusy("post"); setError("");
    try {
      await api.post(`/communities/${cid}/announcements`, draft);
      setDraft({ title: "", body: "", pinned: false });
      setComposing(false);
      await load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const remove = async (id) => {
    setBusy(id);
    try { await api.delete(`/communities/${cid}/announcements/${id}`); await load(); }
    catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-gold" />;

  return (
    <div data-testid="community-news" className="space-y-4">
      <Err testId="news-error">{error}</Err>

      {data.can_post && (
        <Panel testId="news-compose" title="Post an announcement"
          sub="Goes to every member's phone, and you can see who has read it."
          action={
            <Button data-testid="news-compose-toggle" size="sm" variant="outline"
              onClick={() => setComposing((v) => !v)}>
              {composing ? "Cancel" : <><Megaphone className="mr-1.5 h-3.5 w-3.5" /> Write one</>}
            </Button>
          }>
          {composing && (
            <div className="space-y-3">
              <Input data-testid="news-title" placeholder="Headline" value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              <Textarea data-testid="news-body" rows={5} placeholder="What the members need to know."
                value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
              <label className="flex items-center gap-2 text-sm text-slate-400">
                <input data-testid="news-pinned" type="checkbox" checked={draft.pinned}
                  onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })}
                  className="h-4 w-4 accent-[color:var(--gold,#C8A24A)]" />
                Keep it at the top
              </label>
              <Button data-testid="news-post" onClick={post}
                disabled={draft.title.trim().length < 3 || !draft.body.trim() || busy === "post"}>
                {busy === "post" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Publish
              </Button>
            </div>
          )}
        </Panel>
      )}

      {data.announcements.length === 0 ? (
        <Empty testId="news-empty">No announcements yet.</Empty>
      ) : (
        data.announcements.map((a) => (
          <Panel key={a.id} testId={`news-row-${a.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {a.pinned && <Pin className="h-3.5 w-3.5 text-gold" />}
                  <h3 className="font-display text-base text-slate-100 md:text-lg">{a.title}</h3>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  {a.author} · {when(a.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Chip testId={`news-reads-${a.id}`}>{a.reads} read</Chip>
                {data.can_post && (
                  <button data-testid={`news-delete-${a.id}`} onClick={() => remove(a.id)}
                    className="text-slate-500 hover:text-red-300">
                    {busy === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{a.body}</p>
          </Panel>
        ))
      )}
    </div>
  );
};
